import {
  Box,
  IconButton,
  useTheme,
  Menu,
  MenuItem,
  Avatar,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Divider,
  Slide,
  Fade,
  CircularProgress,
} from "@mui/material";
import { useContext, useEffect, useRef, useState, forwardRef } from "react";
import { ColorModeContext, tokens } from "../../theme";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import SettingsBrightnessOutlinedIcon from "@mui/icons-material/SettingsBrightnessOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
// import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import {
  getYourProfile,
  isAuthenticated,
  logout,
} from "../../services/UserService";
import { searchRecords } from "../../services/SearchService";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/Search/SearchBar";
import SearchResults from "../../components/Search/SearchResults";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Topbar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [phase, setPhase] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [appearanceAnchorEl, setAppearanceAnchorEl] = useState(null);
  
  const navigate = useNavigate();
  const [user, setUser] = useState({ fullName: "", role: "" });
  const authenticated = isAuthenticated();

  const [searchResults, setSearchResults] = useState(null);
  const [query, setQuery] = useState("");
  const searchResultsRef = useRef(null);
  const searchBarRef = useRef(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await getYourProfile();
        if (response && response.data) {
          setUser({
            fullName: response.data.fullName,
            role: response.data.role,
          });
        }
      } catch (error) {
        console.error("Failed to fetch user info:", error);
      }
    };

    if (authenticated) {
      fetchProfile();
    }
  }, [authenticated]);

  const handleSearch = async (searchQuery) => {
    setQuery(searchQuery);
    try {
      const response = await searchRecords(searchQuery);
      if (response && response.data) {
        setSearchResults(response.data);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error("Error fetching search results:", error);
      setSearchResults([]);
    }
  };

  const handleClickOutside = (event) => {
    if (
      searchResultsRef.current &&
      !searchResultsRef.current.contains(event.target) &&
      !searchBarRef.current.contains(event.target)
    ) {
      setSearchResults(null);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAppearanceMenuOpen = (event) => {
    setAppearanceAnchorEl(event.currentTarget);
  };

  const handleAppearanceMenuClose = () => {
    setAppearanceAnchorEl(null);
  };

  const handleThemeSelect = (mode) => {
    colorMode.setThemePreference(mode);
    handleAppearanceMenuClose();
  };

  const openLogoutDialog = () => {
    handleProfileMenuClose();
    setIsDialogOpen(true);
  };

  const closeLogoutDialog = () => {
    setIsDialogOpen(false);
  };

  const handleConfirmLogout = () => {
    setPhase("loading");
    setTimeout(() => {
      setPhase(null);
      logout();
      handleProfileMenuClose();
      closeLogoutDialog();
      navigate("/login");
    }, 1500);
  };

  const handleUserProfile = () => {
    handleProfileMenuClose();
    navigate("/profile");
  };

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      p={2}
      sx={{
        borderBottom: `1px solid ${theme.palette.mode === "dark" ? colors.primary[400] : colors.primary[700]}`,
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(15, 23, 42, 0.65)"
            : "rgba(255, 255, 255, 0.7)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        position: "sticky",
        top: 0,
        zIndex: 50,
        boxShadow:
          theme.palette.mode === "dark"
            ? "none"
            : "0 4px 20px rgba(0,0,0,0.03)",
      }}
    >
      {/* SEARCH BAR */}
      <Box
        display="flex"
        alignItems="center"
        sx={{
          flexGrow: 1,
          position: "relative",
          maxWidth: "400px",
          "& .MuiInputBase-root, & .MuiOutlinedInput-root": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.05)"
                : "#ffffff",
            borderRadius: "8px",
            transition: "all 0.2s ease",
            ...(theme.palette.mode !== "dark" && {
              border: "1px solid rgba(0,0,0,0.12)",
              boxShadow: "inset 0 1px 2px rgba(0,0,0,0.03)",
            }),
          },
        }}
      >
        <SearchBar onSearch={handleSearch} ref={searchBarRef} />

        {searchResults && query && (
          <Box
            ref={searchResultsRef}
            sx={{
              position: "absolute",
              top: "calc(100% + 8px)",
              left: 0,
              width: "100%",
              maxHeight: "350px",
              overflowY: "auto",
              backgroundColor:
                theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 8px 32px rgba(0,0,0,0.4)"
                  : "0 8px 32px rgba(0,0,0,0.1)",
              zIndex: 100,
              borderRadius: "8px",
              border: `1px solid ${theme.palette.mode === "dark" ? colors.primary[500] : colors.gray[300]}`,
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: colors.gray[500],
                borderRadius: "10px",
              },
            }}
          >
            <SearchResults
              searchResults={searchResults}
              query={query}
              userRole={user.role}
            />
          </Box>
        )}
      </Box>

      {/* ICONS */}
      <Box display="flex" gap="10px">
        <IconButton
          onClick={handleAppearanceMenuOpen}
          sx={{
            borderRadius: "8px",
            padding: "8px 12px",
            transition: "all 0.2s ease",
            backgroundColor: Boolean(appearanceAnchorEl)
              ? theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.04)"
              : "transparent",
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(0,0,0,0.04)",
            },
            "&:active": {
              transform: "scale(0.92)",
            },
          }}
        >
          {colorMode.themePreference === "system" ? (
            <SettingsBrightnessOutlinedIcon sx={{ fontSize: "22px" }} />
          ) : colorMode.themePreference === "dark" ? (
            <DarkModeOutlinedIcon sx={{ fontSize: "22px" }} />
          ) : (
            <LightModeOutlinedIcon sx={{ fontSize: "22px" }} />
          )}
        </IconButton>

        {authenticated && (
          <IconButton
            onClick={handleProfileMenuOpen}
            sx={{
              borderRadius: "8px",
              padding: "8px 12px",
              transition: "all 0.2s ease",
              backgroundColor: Boolean(anchorEl)
                ? theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(0,0,0,0.04)"
                : "transparent",
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(0,0,0,0.04)",
              },
              "&:active": {
                transform: "scale(0.92)",
              },
            }}
          >
            <PersonOutlinedIcon sx={{ fontSize: "22px" }} />
          </IconButton>
        )}
      </Box>

      {/* APPEARANCE MENU */}
      <Menu
        anchorEl={appearanceAnchorEl}
        open={Boolean(appearanceAnchorEl)}
        onClose={handleAppearanceMenuClose}
        disableScrollLock={true}
        TransitionComponent={Fade}
        transitionDuration={300}
        PaperProps={{
          sx: {
            mt: 1.5,
            minWidth: 180,
            backgroundColor:
              theme.palette.mode === "dark"
                ? `rgba(${parseInt(colors.primary[400].slice(1, 3), 16)}, ${parseInt(colors.primary[400].slice(3, 5), 16)}, ${parseInt(colors.primary[400].slice(5, 7), 16)}, 0.8)`
                : "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            backgroundImage: "none",
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.15)"
                : "rgba(0, 0, 0, 0.1)"
            }`,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 8px 32px 0 rgba(0, 0, 0, 0.4)"
                : "0 8px 32px 0 rgba(0, 0, 0, 0.15)",
            borderRadius: "12px",
            "& .MuiMenuItem-root": {
              padding: "12px 20px",
              gap: "12px",
              margin: "4px 8px",
              borderRadius: "8px",
              transition: "all 0.2s ease-in-out",
              color: `${colors.gray[100]} !important`,
              fontWeight: 600,
              fontSize: "14px",
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.1) !important"
                    : "rgba(0, 0, 0, 0.04) !important",
              },
              "&:active": { transform: "scale(0.95)" },
              "&.Mui-selected": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.15)"
                    : "rgba(0, 0, 0, 0.08)",
                color: `${colors.blueAccent[500]} !important`,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem 
          selected={colorMode.themePreference === 'light'}
          onClick={() => handleThemeSelect('light')}
        >
          <LightModeOutlinedIcon fontSize="small" />
          <Typography fontSize="14px" fontWeight="600">Light Mode</Typography>
        </MenuItem>
        <MenuItem 
          selected={colorMode.themePreference === 'dark'}
          onClick={() => handleThemeSelect('dark')}
        >
          <DarkModeOutlinedIcon fontSize="small" />
          <Typography fontSize="14px" fontWeight="600">Dark Mode</Typography>
        </MenuItem>
        <MenuItem 
          selected={colorMode.themePreference === 'system'}
          onClick={() => handleThemeSelect('system')}
        >
          <SettingsBrightnessOutlinedIcon fontSize="small" />
          <Typography fontSize="14px" fontWeight="600">System</Typography>
        </MenuItem>
      </Menu>

      {/* PROFILE MENU */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        disableScrollLock={true}
        TransitionComponent={Fade}
        transitionDuration={300}
        PaperProps={{
          sx: {
            mt: 1.5,
            minWidth: 220,
            backgroundColor:
              theme.palette.mode === "dark"
                ? `rgba(${parseInt(colors.primary[400].slice(1, 3), 16)}, ${parseInt(colors.primary[400].slice(3, 5), 16)}, ${parseInt(colors.primary[400].slice(5, 7), 16)}, 0.8)`
                : "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            backgroundImage: "none",
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.15)"
                : "rgba(0, 0, 0, 0.1)"
            }`,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 8px 32px 0 rgba(0, 0, 0, 0.4)"
                : "0 8px 32px 0 rgba(0, 0, 0, 0.15)",
            borderRadius: "12px",
            "& .MuiMenuItem-root": {
              padding: "12px 20px",
              gap: "12px",
              margin: "4px 8px",
              borderRadius: "8px",
              transition: "all 0.2s ease-in-out",
              color: `${colors.gray[100]} !important`,
              fontWeight: 600,
              fontSize: "14px",
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.1) !important"
                    : "rgba(0, 0, 0, 0.04) !important",
              },
              "&:active": { transform: "scale(0.95)" },
              "&.logout-item": {
                color: `${colors.redAccent[500]} !important`,
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(244, 63, 94, 0.15) !important"
                      : "rgba(225, 29, 72, 0.1) !important",
                },
              }
            },
            "& .MuiDivider-root": {
              margin: "4px 8px",
              borderColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(0, 0, 0, 0.1)",
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem sx={{ pointerEvents: "none", padding: "12px 16px !important" }}>
          <Avatar
            sx={{
              bgcolor: colors.blueAccent[500],
              width: 36,
              height: 36,
              fontSize: "16px",
              fontWeight: "bold",
            }}
          >
            {user.fullName ? user.fullName.charAt(0).toUpperCase() : "U"}
          </Avatar>
          <Box sx={{ ml: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography fontSize="14px" fontWeight="700" color={colors.gray[100]}>
              {user.fullName}
            </Typography>
            <Typography
              variant="caption"
              color={user.role === "ADMIN" ? colors.blueAccent[500] : colors.greenAccent[500]}
              sx={{
                display: "inline-block",
                backgroundColor: user.role === "ADMIN"
                  ? (theme.palette.mode === "dark" ? "rgba(99, 102, 241, 0.15)" : "rgba(79, 70, 229, 0.1)")
                  : (theme.palette.mode === "dark" ? "rgba(76, 206, 172, 0.15)" : "rgba(5, 150, 105, 0.1)"),
                padding: "2px 8px",
                borderRadius: "12px",
                mt: 0.5,
                fontWeight: "bold",
                width: "fit-content",
              }}
            >
              {user.role}
            </Typography>
          </Box>
        </MenuItem>

        <Divider />

        {authenticated && (
          <MenuItem onClick={handleUserProfile}>
            <PersonOutlinedIcon fontSize="small" />
            <Typography fontSize="14px" fontWeight="600">
              User Profile
            </Typography>
          </MenuItem>
        )}
        {authenticated && (
          <MenuItem
            onClick={openLogoutDialog}
            className="logout-item"
          >
            <LogoutOutlinedIcon fontSize="small" />
            <Typography fontSize="14px" fontWeight="600">
              Log out
            </Typography>
          </MenuItem>
        )}
      </Menu>

      <Dialog
        open={isDialogOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown" || phase === "loading") return;
          closeLogoutDialog();
        }}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Transition}
        keepMounted
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: { xs: "100%", sm: "480px !important" },
            backgroundColor:
              theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
            backgroundImage: "none",
            borderRadius: "16px",
            boxShadow: theme.palette.mode === "dark" 
                  ? "0 24px 48px rgba(0,0,0,0.5)" 
                  : "0 24px 48px rgba(0,0,0,0.1)",
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.05)"
            }`,
            p: { xs: 2, sm: 3 },
          },
        }}
      >
        <DialogTitle
          id="logout-dialog-title"
          sx={{ p: 0, m: 0, textAlign: "center" }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: { xs: 1.5, sm: 2 },
            }}
          >
            <Box
              sx={{
                width: { xs: 56, sm: 64 },
                height: { xs: 56, sm: 64 },
                borderRadius: "50%",
                backgroundColor: theme.palette.mode === "dark" ? "rgba(244, 63, 94, 0.1)" : "rgba(225, 29, 72, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LogoutOutlinedIcon sx={{ fontSize: { xs: 28, sm: 32 }, color: colors.redAccent[500] }} />
            </Box>
          </Box>
          <Typography
            variant="h3"
            fontWeight="800"
            color={colors.gray[100]}
            sx={{ mb: 1, fontSize: { xs: "1.3rem", sm: "1.5rem" } }}
          >
            Confirm Logout
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: "0 !important", m: 0, mt: 1 }}>
          <DialogContentText
            id="logout-dialog-description"
            textAlign="center"
            sx={{
              color: colors.gray[300],
              fontSize: { xs: "0.9rem", sm: "1rem" },
              lineHeight: 1.5,
              px: { xs: 0, sm: 4 },
              m: 0,
            }}
          >
            Are you sure you want to end your current session?
          </DialogContentText>
        </DialogContent>

        <Divider 
          sx={{ 
            mt: { xs: 3, sm: 4 }, 
            mb: { xs: 2, sm: 3 }, 
            borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.12)" 
          }} 
        />

        <DialogActions 
          sx={{ 
            p: 0, 
            m: 0, 
            pb: { xs: 2, sm: 1 }, 
            px: 0,
            justifyContent: "center", 
            flexDirection: { xs: "column-reverse", sm: "row" },
            gap: { xs: 2, sm: 3 }, 
            width: "100%",
            "& > :not(style) ~ :not(style)": {
              ml: "0 !important",
            }
          }}
        >
          <Button
            onClick={closeLogoutDialog}
            disabled={phase === "loading"}
            variant="outlined"
            sx={{
              width: "100%",
              color: colors.gray[100],
              borderColor: colors.gray[500],
              textTransform: "none",
              whiteSpace: "nowrap",
              fontSize: { xs: "0.9rem", sm: "1rem" },
              fontWeight: "600",
              px: { xs: 2, sm: 4 },
              py: { xs: 1.2, sm: 1.4 },
              borderRadius: "8px",
              minWidth: { xs: "120px", sm: "140px" },
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                borderColor: colors.gray[300],
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(0,0,0,0.04)",
              },
              "&:active": {
                transform: "scale(0.95)",
              },
              "&.Mui-disabled": {
                borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)",
                color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmLogout}
            disabled={phase === "loading"}
            variant="contained"
            sx={{
              width: "100%",
              backgroundColor: colors.redAccent[500],
              color: "#ffffff",
              textTransform: "none",
              whiteSpace: "nowrap",
              fontSize: { xs: "0.9rem", sm: "1rem" },
              fontWeight: "600",
              px: { xs: 2, sm: 4 },
              py: { xs: 1.2, sm: 1.4 },
              borderRadius: "8px",
              minWidth: { xs: "120px", sm: "140px" },
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 4px 10px rgba(0,0,0,0.3)"
                  : "0 4px 12px rgba(225, 29, 72, 0.2)",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                backgroundColor: colors.redAccent[600],
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 6px 14px rgba(0,0,0,0.4)"
                    : "0 6px 16px rgba(225, 29, 72, 0.3)",
                transform: "translateY(-1px)",
              },
              "&:active": {
                transform: "scale(0.95)",
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)",
                color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
                boxShadow: "none",
              }
            }}
          >
            {phase === "loading" ? (
              <>
                Logging out&nbsp;
                <CircularProgress size={18} sx={{ color: "inherit", ml: "8px" }} />
              </>
            ) : (
              "Logout"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Topbar;